// @aiContributed-2025-10-27
import { isProdEnvironment, isSiEnvironment } from '@bingads-webui-campaign/campaign-ui-common';
import type { GlobalAppServerContext } from './types';

const INSTANCE_ID = {
  PROD: '9c82544d-96ad-40a1-802b-ed037146f100',
  INT: '32ba48e5-ec94-4948-8788-f574ab972e45',
  CI: '91494e38-e7f8-4bf9-b890-fc2dfa3351a9',
};

export type DfpWindow = Window & {
  dfp?: {
    doFpt: (_document: Document) => void;
  };
};

const dfpWindow = window as DfpWindow;

// @aiContributed-MAPFuse-2026-03-30
export type ServerContext = Pick<GlobalAppServerContext, 'Omniture'>;

function initialize(sessionId: string, instanceId: string, callback?: () => void): void {
  try {
    // Create script element
    const dfpScriptElement: HTMLScriptElement = document.createElement('script');
    dfpScriptElement.async = true;
    dfpScriptElement.src = `https://fpt.dfp.microsoft.com/mdt.js?session_id=${encodeURIComponent(sessionId)}&instanceId=${encodeURIComponent(instanceId)}`;

    // Handle script load success
    dfpScriptElement.onload = (): void => {
      if (callback) {
        callback();
      }
    };

    // Handle script load error
    dfpScriptElement.onerror = (): void => {
      console.warn('Failed to load DFP script');
    };

    // Insert script into document
    const firstScript: HTMLScriptElement | undefined = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(dfpScriptElement, firstScript);
    } else {
      document.head.appendChild(dfpScriptElement);
    }
  } catch (error) {
    console.warn('Error initializing DFP script:', error);
  }
}

export function callDfp(): void {
  if (dfpWindow.dfp && typeof dfpWindow.dfp.doFpt === 'function') {
    try {
      dfpWindow.dfp.doFpt(document);
    } catch (error) {
      console.warn('Error calling DFP doFpt:', error);
    }
  } else {
    console.warn('DFP is not available or doFpt method is not found');
  }
}

export function setupDfp(serverContext: ServerContext): void {
  if (!serverContext || !serverContext.Omniture || !serverContext.Omniture.SessionId) {
    console.error('ServerContext with Omniture.SessionId is required for DFP setup');
    return;
  }

  const sessionId = serverContext.Omniture.SessionId;
  // Set instanceId based on production environment
  let instanceId = INSTANCE_ID.CI;
  if (isProdEnvironment(window.location.hostname)) {
    instanceId = INSTANCE_ID.PROD;
  } else if (isSiEnvironment(window.location.hostname)) {
    instanceId = INSTANCE_ID.INT;
  }

  // Initialize and load the DFP script, call doFpt after it loads
  initialize(sessionId, instanceId, callDfp);
}
